import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    clientSecret: String,
    successUrl: String,
    publishableKey: String,
    shippingUrl: String,
    cartId: Number,
    email: String,
    deliveryCountry: String
  }

  static targets = ["linkAuth", "addressElement", "paymentElement", "submitButton", "errors", "overlay"]

  connect() {
    this.initializeElements()
  }

  initializeElements() {
    this.stripe = Stripe(this.publishableKeyValue)
    // 16px on mobile (anything smaller makes iOS Safari zoom the field on focus),
    // 14.5px on desktop where that zoom never happens. Same approach as bd-stripe.
    // 768px, matching the breakpoint the rest of the app uses for exactly this rule. At the
    // old 640 an iPhone held sideways — 667px to 932px wide — counted as desktop and got the
    // smaller size, so the card fields zoomed the viewport on focus while every other input
    // on the page did not.
    const isMobile = window.matchMedia && window.matchMedia("(max-width: 768px)").matches
    const inputSize = isMobile ? "16px" : "14.5px"
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      appearance: {
        theme: "flat",
        variables: {
          fontFamily: "'DM Sans', sans-serif",
          fontSizeBase: inputSize,
          fontSizeSm: "12px",
          colorPrimary: "#C88B7E",
          colorText: "#1C1A18",
          colorTextSecondary: "#6B5E52",
          colorTextPlaceholder: "#B8A99A",
          colorBackground: "#FFFFFF",
          borderRadius: "8px",
          spacingUnit: "4px",
          spacingGridRow: "14px",
          spacingGridColumn: "14px"
        },
        rules: {
          ".Input": {
            border: "1.5px solid #E8DDD6",
            padding: "11px 14px",
            fontSize: inputSize,
            transition: "border-color 0.2s"
          },
          ".Input:focus": {
            border: "1.5px solid #1C1A18",
            boxShadow: "none"
          },
          ".Label": {
            fontSize: "12px",
            fontWeight: "500",
            color: "#6B5E52"
          }
        }
      },
      locale: "fr"
    })

    const linkAuthElement = this.elements.create("linkAuthentication", {
      defaultValues: { email: this.emailValue || "" }
    })
    linkAuthElement.mount(this.linkAuthTarget)

    const country = this.deliveryCountryValue || "FR"
    const addressElement = this.elements.create("address", {
      mode: "shipping",
      // Stripe's built-in address autocomplete (free, no Google key). Off by default;
      // "automatic" turns on the typeahead suggestions that fill the fields.
      autocomplete: { mode: "automatic" },
      fields: { phone: "always" },
      validation: { phone: { required: "always" } },
      display: { name: "split" },
      defaultValues: { address: { country } },
      allowedCountries: [country]
    })
    addressElement.mount(this.addressElementTarget)

    const paymentElement = this.elements.create("payment", {
      terms: { card: "never" },
      fields: { billingDetails: { address: "if_required" } }
    })
    paymentElement.mount(this.paymentElementTarget)

    // Clear the error the moment the customer edits anything. Cleared only at the top of
    // submit(), it outlived whatever caused it: someone told their e-mail was incomplete,
    // who then fixed it, kept reading a complaint about a field that was already correct
    // (reported on the Cosy Fantasy tunnel, which had the identical gap). Every element
    // gets it because the message names whichever field failed first, and the one being
    // corrected is not necessarily the one it is about.
    const clearOnEdit = () => {
      if (this.errorsTarget.textContent !== "") this.errorsTarget.textContent = ""
    }
    linkAuthElement.on("change", clearOnEdit)
    addressElement.on("change", clearOnEdit)
    paymentElement.on("change", clearOnEdit)
  }

  async submit(event) {
    event.preventDefault()
    this.errorsTarget.textContent = ""
    this.setSubmitButtonsDisabled(true)

    // Any rejection (Stripe.js, address getValue, the address fetch, confirmPayment)
    // must re-enable the button — otherwise the customer is stuck with a dead button
    // and can only retry by reloading the page.
    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        this.showError(submitError.message)
        return
      }

      // Save shipping address to cart before confirming with Stripe
      const addressElement = this.elements.getElement("address")
      const { complete, value: addressValue } = await addressElement.getValue()
      if (complete) {
        await this.saveShippingAddress(addressValue)
      }

      this.showOverlay()
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: { return_url: this.successUrlValue }
      })

      // We only get here if confirmPayment did NOT redirect (i.e. it errored).
      if (result.error) {
        this.hideOverlay()
        this.showError(result.error.message)
      }
    } catch (err) {
      this.hideOverlay()
      this.showError("Une erreur est survenue. Veuillez réessayer.")
    }
  }

  async saveShippingAddress(addressValue) {
    const meta = document.querySelector("meta[name='csrf-token']")
    const csrfToken = meta && meta.content

    const response = await fetch(this.shippingUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        name: addressValue.name,
        address: addressValue.address,
        mobile_phone: addressValue.phone
      })
    })

    // A 4xx/5xx fetch resolves (doesn't reject), so check explicitly — otherwise we'd
    // confirm the payment with the shipping address never saved to the cart.
    if (!response.ok) throw new Error("Failed to save shipping address")
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("visible")
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("visible")
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.setSubmitButtonsDisabled(false)
  }

  setSubmitButtonsDisabled(disabled) {
    this.submitButtonTargets.forEach((button) => {
      button.disabled = disabled
    })
  }
}
